import { lazy } from "react";

/**
 * Wraps React.lazy so that a failed dynamic import (ChunkLoadError) — which
 * usually happens when a new build is deployed and the old chunk hashes no
 * longer exist, or on a flaky network — triggers a single full page reload to
 * fetch the fresh index.html + chunk manifest, instead of crashing the app.
 *
 * A sessionStorage flag prevents an infinite reload loop: we only auto-reload
 * once; if it still fails after reload, the real error is surfaced.
 */
const RELOAD_KEY = "chunk_failed_reloaded";

const lazyWithRetry = (importer) =>
  lazy(async () => {
    try {
      const component = await importer();
      // Loaded fine — clear the guard so future stale deploys can reload again.
      window.sessionStorage.removeItem(RELOAD_KEY);
      return component;
    } catch (error) {
      const message = error?.message || "";
      const isChunkError =
        error?.name === "ChunkLoadError" ||
        /Loading chunk .* failed/i.test(message) ||
        /Loading CSS chunk .* failed/i.test(message) ||
        /failed to fetch dynamically imported module/i.test(message);

      if (isChunkError) {
        const alreadyReloaded =
          window.sessionStorage.getItem(RELOAD_KEY) === "true";
        if (!alreadyReloaded) {
          window.sessionStorage.setItem(RELOAD_KEY, "true");
          window.location.reload();
          // Keep Suspense pending while the page reloads.
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });

export default lazyWithRetry;
